import { ChevronDown, KeyRound, LogOut, Settings, User } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";

import { PATHS } from "@/app/routes/paths";
import { ChangePasswordDialog } from "@/features/auth/ChangePasswordDialog";
import { IRWMS_PORTAL_URL, sessionCameFromSso } from "@/core/sso/irwms";
import { Avatar, AvatarFallback } from "@/shared/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { useAuth } from "@/shared/contexts/AuthContext";

interface HeaderProps {
  title: string;
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "?"
  );
}

export function Header({ title }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const displayName = user?.name ?? "Guest";
  const roleName = user?.roles[0]?.name;

  const handleLogout = () => {
    const viaSso = sessionCameFromSso();
    void logout().finally(() => {
      // An SSO session is ended by the identity provider redirect inside logout();
      // only a local session needs to be sent to the login form here.
      if (!viaSso) navigate(PATHS.auth.login, { replace: true });
    });
  };

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white">
      <div className="flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          <h1 className="text-slate-900">{title}</h1>
          {IRWMS_PORTAL_URL && (
            <a
              href={IRWMS_PORTAL_URL}
              className="text-xs font-medium text-slate-500 hover:text-blue-700 hover:underline"
            >
              ← Back to IRWMS portal
            </a>
          )}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg px-3 py-2 transition-colors hover:bg-slate-50">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-blue-600 text-sm text-white">
                {initialsOf(displayName)}
              </AvatarFallback>
            </Avatar>
            <span className="text-sm text-slate-700">{displayName}</span>
            <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <span className="block">{displayName}</span>
              {roleName && (
                <span className="block text-xs font-normal text-slate-500">{roleName}</span>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2">
              <User className="h-4 w-4" aria-hidden="true" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2">
              <Settings className="h-4 w-4" aria-hidden="true" />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2" onClick={() => setChangePasswordOpen(true)}>
              <KeyRound className="h-4 w-4" aria-hidden="true" />
              <span>Change password</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 text-red-600" onClick={handleLogout}>
              <LogOut className="h-4 w-4" aria-hidden="true" />
              <span>Logout</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </header>
  );
}
